import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi, type ArrivalAnnotation } from '@/shared/api';
import { downscalePortalPhoto } from '@/shared/lib/downscaleImage';

/** Wie `ArrivalImageService.MaxImagesPerStep` — mehr nimmt der Server nicht an. */
export const MAX_ARRIVAL_IMAGES = 8;

/**
 * Bilder eines Schritts. Der Schlüssel trägt nur die Schritt-Id — welcher Riegel gilt, weiß der
 * Server aus dem Schritt, und das Frontend muss die Ebene deshalb nicht kennen.
 */
export function useArrivalImages(stepId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const queryKey = ['arrival-steps', stepId, 'images'];
  const invalidate = () => qc.invalidateQueries({ queryKey });
  const onError = async (error: unknown) => {
    const parsed = await toApiError(error);
    toast.error(parsed.detail ?? parsed.title);
  };

  const query = useQuery({ queryKey, queryFn: () => api.arrivalSteps.images.list(stepId) });

  const upload = useMutation({
    // Anfahrhilfe-Bilder sind reine Anzeigebilder: der Gast schaut sie unterwegs auf dem Telefon
    // an, und dort ist mehr als 2560 px keine Information, sondern Wartezeit. Ein Kamerafoto
    // scheiterte hier bisher an derselben Groessengrenze wie in der Akte.
    mutationFn: async (file: File) =>
      api.arrivalSteps.images.upload(stepId, await downscalePortalPhoto(file)),
    // Die Erfolgsmeldung gibt das Panel einmal für den ganzen Stapel.
    onSuccess: () => void invalidate(),
    onError,
  });

  const remove = useMutation({
    mutationFn: (imageId: string) => api.arrivalSteps.images.remove(stepId, imageId),
    onSuccess: () => void invalidate(),
    onError,
  });

  const move = useMutation({
    mutationFn: (input: { imageId: string; direction: 'Up' | 'Down' }) =>
      api.arrivalSteps.images.move(stepId, input.imageId, input.direction),
    onSuccess: () => void invalidate(),
    onError,
  });

  const annotate = useMutation({
    mutationFn: (input: { imageId: string; annotations: ArrivalAnnotation[] }) =>
      api.arrivalSteps.images.setAnnotations(stepId, input.imageId, input.annotations),
    onSuccess: () => {
      toast.success('Markierungen gespeichert');
      void invalidate();
    },
    onError,
  });

  return { query, upload, remove, move, annotate };
}
